import React, {useContext, useEffect, useMemo} from 'react';
import {useFieldArray, useFormContext} from 'react-hook-form';
import {ListConfigFieldTypes, ListConfigNamedColumn} from '@src/@types/components/List';
import {IFormFieldItemPropsContext} from '@src/@interfaces/contexts/IFormFieldItemPropsContext';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import ConfirmableButton from '@src/components/ConfirmableButton/ConfirmableButton';
import ControlledList from '@src/components/ControlledList/ControlledList';
import {ControlledListProps} from '@src/@types/components/ControlledList';
import FormField, {Props} from '@src/components/Form/FormField';
import {FormFieldItemPropsContext} from '@src/contexts/FormFieldItemPropsContext';
import {FormTabsContext} from '@src/contexts/FormTabsContext';
import {LabelField} from '@views/payment-types/fields/additional-fields/LabelField';
import {IsRequiredField} from '@views/payment-types/fields/additional-fields/IsRequiredField';
import {IPaymentTypeModel} from '@src/@interfaces/models/PaymentType/IPaymentTypeModel';
import {IPaymentTypeAdditionalFieldModel} from '@src/@interfaces/models/PaymentType/IPaymentTypeAdditionalFieldModel';

const columns = [
    {
        name: 'id',
        label: '#',
        type: ListConfigFieldTypes.CUSTOM,
        invisible: true,
    },
    {
        name: 'label',
        label: 'PaymentTypesShow.fields.additional-fields.label',
        type: ListConfigFieldTypes.CUSTOM,
    },
    {
        name: 'is_required',
        label: 'PaymentTypesShow.fields.additional-fields.is_required',
        type: ListConfigFieldTypes.SWITCH,
    },
];

function getCustomColumnComponent(config: ListConfigNamedColumn) {
    switch (config.name) {
        case 'id':
            return null;

        case 'label':
            return LabelField;

        case 'is_required':
            return IsRequiredField;
    }

    return null;
}

function ActualForm(props: Props<IPaymentTypeModel>) {
    const tabsContext = useContext(FormTabsContext);
    const [designContext] = useContext(FormFieldDesignContext);
    const {control} = useFormContext<IPaymentTypeModel>();
    const {fields, append, remove} = useFieldArray({
        control,
        name: props.fieldName as 'additional_fields',
        keyName: 'autoGeneratedUuid',
    });

    const tabName = props.tabName;
    useEffect(() => {
        if (tabsContext && tabName) {
            tabsContext.setBadge(tabName, {
                color: 'primary',
                label: String(fields.length)
            });
        }
    }, [tabsContext, tabName, fields.length]);

    const isDisabled = props.forceDisabled || designContext.fieldsAreDisabled || designContext.fieldsAreReadOnly;
    const [listContentProps, formFieldArrayContextValues] = useMemo(() => {
        const listContentProps: ControlledListProps<IPaymentTypeAdditionalFieldModel>['listContentProps'] = {
            tableHeadProps: {
                additionalControlNode: <button
                        type="button"
                        className="btn btn-sm btn-outline-success me-1"
                        disabled={isDisabled}
                        onClick={() => append({
                            id: null,
                            type: 'input',
                            label: '',
                            is_required: false,
                        })}
                >
                    <i className="fa fa-plus"/>
                </button>,
            },
            tableBodyProps: {
                renderControls(record, index) {
                    if (record?.id) {
                        return <ConfirmableButton
                                size="sm"
                                color="danger"
                                disabled={isDisabled}
                                onClick={() => remove(index)}
                        >
                            <i className="fa fa-times"/>
                        </ConfirmableButton>;
                    }

                    return <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            disabled={isDisabled}
                            onClick={() => remove(index)}
                    >
                        <i className="fa fa-times"/>
                    </button>;
                }
            }
        };

        const formFieldArrayContextValues: IFormFieldItemPropsContext<IPaymentTypeModel> = {
            control,
            fieldName: props.fieldName,
            formName: props.formName,
            tabName: props.tabName,
            model: props.model,
            modelOptions: props.modelOptions,
            config: props.config,
            forceReadonly: props.config.readOnly === true || props.forceReadonly === true,
            forceDisabled: props.config.disabled === true || props.forceDisabled === true,
        };

        return [listContentProps, formFieldArrayContextValues];
    }, [isDisabled, props, control, append, remove]);

    return <FormFieldItemPropsContext.Provider value={formFieldArrayContextValues}>
        <ControlledList<IPaymentTypeAdditionalFieldModel>
                uniqueListName={`${props.formName}@${props.fieldName}--contacts-list`}
                controllerName={props.formName}
                records={fields}
                columns={columns}
                withoutSearchField={true}
                keepInParentBox={true}
                listContentProps={listContentProps}
                listContextValues={{getCustomColumnComponent}}
                bordered={true}
                className="mb-1"
        />
    </FormFieldItemPropsContext.Provider>;
}

export default function PaymentTypeAdditionalFieldsField<Model extends IPaymentTypeModel = IPaymentTypeModel>(props: Props<Model>) {
    return <FormField {...props} withoutLayout={true} fieldElement={ActualForm as any}/>;
}
